import { EscposBuilder, textBoxForPaper } from './bytes'
import { renderKotSlipText, type KotSlip } from '@/lib/kotSlip'

/**
 * A kitchen ticket as ESC/POS bytes (F-FE-N111).
 *
 * ── Why this exists ──
 *
 * A printer KOT reaches the tablet as a 32-column string rendered by the
 * backend, and the Android agent turns it into bytes with a fixed preamble:
 *
 *     ESC @ | ESC a 0 | ESC ! 0x18 | <text> | feed | cut
 *
 * No left margin, no print-area width. Two consequences on an 80mm roll, both
 * of which Mizaj photographed:
 *
 *   - 32 columns is 384 of the head's 576 dots, so the ticket uses two thirds
 *     of the paper and item names wrap that need not;
 *   - printing starts at dot zero, so a roll seated a millimetre off centre
 *     eats the first character of every line — ITEM became TEM.
 *
 * Neither is fixable from the string, because the agent owns the bytes. So the
 * frontend builds them instead, from the STRUCTURED slip the backend already
 * ships alongside the text (`StationKOT.slip`), laid out at the width the roll
 * actually has and inset off both edges.
 *
 * ── Why this is additive, not a replacement ──
 *
 * The job still carries its original `text`. An agent below 1.3.0 does not know
 * the `escpos` field and falls through to `text`, printing exactly what it
 * prints today — so a tablet that has not updated keeps working rather than
 * losing its kitchen tickets. Chunkyard is live on this path; there is no
 * version of this change that is allowed to stop their KOTs.
 *
 * ── Why the styling is reproduced rather than improved ──
 *
 * `ESC ! 0x18` is emphasized + double-HEIGHT (not width). Chefs read these from
 * across a hot line and that is the size they are used to, so it is reproduced
 * exactly: bold on, height x2, width x1. The only intended differences are the
 * width of the slip and the margin around it.
 */
export function buildKotEscpos(
  slip: KotSlip,
  paperWidthMm: number,
  offsetCols = 0,
): Uint8Array {
  const box = textBoxForPaper(paperWidthMm)
  // The inset comes out of the ticket's width, so the slip never grows past
  // the head. Literal spaces, for the reason in HtmlToEscposOptions.offsetCols.
  const offset = Math.max(0, Math.min(offsetCols, box.columns - 16))
  const pad = ' '.repeat(offset)
  const text = renderKotSlipText(slip, box.columns - offset)

  const b = new EscposBuilder().init().printArea(box.widthDots, box.leftDots)
  b.align('left').bold(true).size(1, 2)
  // renderKotSlipText always ends with a newline; splitting on it would emit a
  // trailing blank line inside the doubled-height run, which is half a
  // centimetre of paper on every ticket.
  for (const line of text.replace(/\n+$/, '').split('\n')) b.line(pad + line)
  b.size(1, 1).bold(false).cut()
  return b.build()
}
